import { useState, useEffect, useCallback } from "react";
import { authService, SignupData, SigninData } from "../services/auth.service";
import { User } from "../types/api.types";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUser = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (err: any) {
      setError(err.message);
      authService.logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const signup = async (data: SignupData) => {
    setLoading(true);
    setError(null);
    try {
      const { user: userData } = await authService.signup(data);
      setUser(userData);
      return userData;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signin = async (data: SigninData) => {
    setLoading(true);
    setError(null);
    try {
      const { user: userData } = await authService.signin(data);
      setUser(userData);
      return userData;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
  }, []);

  return {
    user,
    loading,
    error,
    signup,
    signin,
    logout,
    isAuthenticated: !!user,
  };
};
